import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Grid, Alert, CircularProgress, Typography, Box,
} from '@mui/material';
import type { Study } from '../../api/types';
import { api } from '../../api/rpc';

type RescheduleAction = 'cancel' | 'unschedule' | 'move';

interface RescheduleOrderDialogProps {
  open: boolean;
  study: Study | null;
  onClose: () => void;
  onComplete: () => void;
}

export const RescheduleOrderDialog = ({ open, study, onClose, onComplete }: RescheduleOrderDialogProps) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<RescheduleAction>('move');
  const [type, setType] = useState('ROUTINE');
  const [reason, setReason] = useState('');
  const [newDate, setNewDate] = useState('');
  const [ae, setAe] = useState('');
  const [duration, setDuration] = useState(30);

  const handleSubmit = async () => {
    if (!study) return;
    setSaving(true);
    setError(null);
    try {
      const dateMs = action === 'move' && newDate ? new Date(newDate).getTime() : undefined;
      if (action === 'move' && (dateMs === undefined || isNaN(dateMs))) {
        setError('Please select a valid date');
        setSaving(false);
        return;
      }
      await api.rescheduleOrder({
        objKey: study.objKey,
        action,
        type: action !== 'move' ? type : undefined,
        reason: reason || undefined,
        date: dateMs,
        ae: action === 'move' && ae ? ae : undefined,
        duration: action === 'move' ? duration : undefined,
      });
      onComplete();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reschedule order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Reschedule Order</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {study && (
          <Box sx={{ mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>{study.name}</strong> — {study.acc} — {study.mod}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Action</InputLabel>
              <Select
                value={action}
                label="Action"
                onChange={e => setAction(e.target.value as RescheduleAction)}
              >
                <MenuItem value="move">Move to new time</MenuItem>
                <MenuItem value="cancel">Cancel order</MenuItem>
                <MenuItem value="unschedule">Unschedule (keep order)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {(action === 'cancel' || action === 'unschedule') && (
            <>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Type"
                  value={type}
                  onChange={e => setType(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
            </>
          )}

          {action === 'move' && (
            <>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="New Date & Time"
                  type="datetime-local"
                  value={newDate}
                  onChange={e => setNewDate(e.target.value)}
                  fullWidth size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="AE Title"
                  value={ae}
                  onChange={e => setAe(e.target.value)}
                  fullWidth size="small"
                  placeholder={study?.ae ?? ''}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Duration (min)</InputLabel>
                  <Select
                    value={duration}
                    label="Duration (min)"
                    onChange={e => setDuration(Number(e.target.value))}
                  >
                    {[15, 30, 45, 60, 90, 120].map(d => (
                      <MenuItem key={d} value={d}>{d} min</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Reason"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  fullWidth size="small"
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={20} /> : action === 'cancel' ? 'Cancel Order' : action === 'unschedule' ? 'Unschedule' : 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
